/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// src/components/EmployeeTimesheetNew.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Stack,
} from "@mui/material";
import Grid from '@mui/material/Grid';
import {
  Person as PersonIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as ClockIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from "@mui/icons-material";
import { db, auth } from "../firebase";
import { functions } from "../firebase";
import { tryResolveTenantId } from '../utils/tenant';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  Timestamp,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  limit
} from "firebase/firestore";
import { DateTime } from 'luxon';
import { setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { updateShiftTimecard } from '../utils/shiftAttendance';
import { useRoles } from "../hooks/useFirestoreData";
import useEmployeeAssignments from '../hooks/useEmployeeAssignments';
import { validateShiftTime } from "../utils/businessDay";
import { getBusinessDateKey } from "../utils/businessDateKey";
import findMatchingShift from '../utils/findMatchingShift';

export default function EmployeeTimesheetNew() {
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [approvalFilter, setApprovalFilter] = useState("all");
  // Use local date (YYYY-MM-DD) rather than UTC-derived ISO date to avoid timezone mismatches
  const formatLocalISODate = (d = new Date()) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const compactToIsoDate = (s) => {
    if (!s || !/^\d{8}$/.test(String(s))) return null;
    const v = String(s);
    return `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`;
  };
  const toDateObj = (value) => {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  // Format a Date for `input[type="datetime-local"]` (local time, no timezone)
  const formatLocalDateTimeValue = (d) => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${hh}:${mm}`;
  };

  // Helper: resolve an IANA timezone from a location record.
  // - If the location already stores an IANA zone (contains '/'), return it.
  // - If the location has `ianaTimezone` use that.
  // - Otherwise, map common display names (e.g. 'Central Time') or GMT offsets to a reasonable IANA default.
  const mapDisplayToIana = (s) => {
    if (!s) return null;
    const low = String(s).toLowerCase();
    if (low.includes('/')) return s; // already likely IANA
    if (low.includes('central')) return 'America/Chicago';
    if (low.includes('eastern')) return 'America/New_York';
    if (low.includes('pacific')) return 'America/Los_Angeles';
    if (low.includes('mountain')) return 'America/Denver';
    if (low.includes('alaska')) return 'America/Anchorage';
    if (low.includes('hawaii')) return 'Pacific/Honolulu';
    // GMT offset patterns like (GMT-06:00)
    const g = low.match(/gmt\s*([+-]?\d{1,2}):?(\d{2})?/);
    if (g) {
      const off = g[1];
      if (off === '-6' || off === '-06') return 'America/Chicago';
      if (off === '-5' || off === '-05') return 'America/New_York';
      if (off === '-8' || off === '-08') return 'America/Los_Angeles';
      if (off === '-7' || off === '-07') return 'America/Denver';
      if (off === '0' || off === '+0' || off === '+00') return 'Etc/UTC';
    }
    return null;
  };

  const resolveLocationIana = (loc) => {
    if (!loc) return null;
    if (loc.ianaTimezone) return loc.ianaTimezone;
    if (loc.timezone && String(loc.timezone).includes('/')) return loc.timezone;
    const candidate = mapDisplayToIana(loc.timezone || loc.locationTimezone || loc.name || '');
    return candidate;
  };

  const resolveLocationBusinessDayCutoff = (loc) => {
    if (!loc) return null;
    const cutoff = loc.businessDayCutoff;
    if (cutoff && typeof cutoff === 'object') {
      const hour = Number(cutoff.hour);
      const minute = Number(cutoff.minute);
      if (!Number.isNaN(hour) && !Number.isNaN(minute)) {
        if (hour === 24 && minute === 0) return '24:00';
        if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
          return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        }
      }
    }
    return loc.endOfBusiness || null;
  };

  const resolveLocationForBusinessDate = async (tenantId, locationId) => {
    if (!locationId) return null;
    const cached = locations.find((l) => l.id === locationId) || null;
    if (cached?.businessDayCutoff) return cached;
    try {
      const locDoc = await getDoc(doc(db, `tenants/${tenantId}/locations`, locationId));
      if (locDoc.exists()) return { id: locationId, ...locDoc.data() };
    } catch (e) {
      // ignore and fall back to cached
    }
    return cached;
  };

  const getTimeEntryBusinessDate = (locationRecord, timestampDate, fallbackTimezone = null) => {
    return getBusinessDateKey(
      locationRecord || { timezone: fallbackTimezone || 'America/Chicago' },
      timestampDate
    );
  };

  const [startDate, setStartDate] = useState(formatLocalISODate());
  const [endDate, setEndDate] = useState(formatLocalISODate());
  const [dateRangeMode, setDateRangeMode] = useState("current"); // current | previous | next | custom
  const [payPeriods, setPayPeriods] = useState([]);
  const [currentPayPeriodIndex, setCurrentPayPeriodIndex] = useState(-1);
  const [shiftData, setShiftData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalHours, setTotalHours] = useState(0);

  // Load roles from Firestore
  const roles = useRoles();

  // Edit dialog states
  const [editDialog, setEditDialog] = useState({ open: false, shift: null, type: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, shift: null });
  const [addShiftDialog, setAddShiftDialog] = useState({ open: false });
  const [editLoading, setEditLoading] = useState(false);
  const [bulkApproveLoading, setBulkApproveLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });

  // Edit form states
  const [editDateTime, setEditDateTime] = useState("");
  const [editClockOutDateTime, setEditClockOutDateTime] = useState("");
  // editType intentionally unused - kept for future expansion
  const [editType, setEditType] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editBreakMinutes, setEditBreakMinutes] = useState(0);
  const [editBreakTypeId, setEditBreakTypeId] = useState("");
  const [editBreakStartTime, setEditBreakStartTime] = useState("");
  const [editBreakError, setEditBreakError] = useState("");

  // Add shift form states
  const [addEmployee, setAddEmployee] = useState("");
  const [addDate, setAddDate] = useState(formatLocalISODate());
  const [addClockInTime, setAddClockInTime] = useState("");
  const [addClockOutTime, setAddClockOutTime] = useState("");
  const [addBreakMinutes, setAddBreakMinutes] = useState(0);
  const [addBreakQuick, setAddBreakQuick] = useState(0);
  const [breakTypes, setBreakTypes] = useState([]);
  const [addBreakTypeId, setAddBreakTypeId] = useState("");
  const [addBreakStartTime, setAddBreakStartTime] = useState("");
  const [addLocationId, setAddLocationId] = useState("");
  const [addRoleId, setAddRoleId] = useState("");
   const [selectedWeekdays, setSelectedWeekdays] = useState([]); // Removed multi-day feature
  const [addBreakError, setAddBreakError] = useState("");

  // Hook: listen to flattened assignments for the currently selected addEmployee
  const { nested: flattenedNestedForAddEmployee, loading: assignmentsLoadingForAddEmployee } = useEmployeeAssignments(addEmployee || null);

  const selectedPayPeriod = useMemo(() => {
    if (!payPeriods.length || currentPayPeriodIndex < 0) return null;
    if (dateRangeMode === 'custom') return null;
    let idx = currentPayPeriodIndex;
    if (dateRangeMode === 'previous') idx = Math.max(0, currentPayPeriodIndex - 1);
    if (dateRangeMode === 'next') idx = Math.min(payPeriods.length - 1, currentPayPeriodIndex + 1);
    return payPeriods[idx] || null;
  }, [payPeriods, currentPayPeriodIndex, dateRangeMode]);

  useEffect(() => {
    if (payPeriods.length === 0 && dateRangeMode !== 'custom') {
      setDateRangeMode('custom');
    }
  }, [payPeriods.length, dateRangeMode]);

  useEffect(() => {
    if (dateRangeMode === 'custom') return;
    if (!selectedPayPeriod) return;
    setStartDate(selectedPayPeriod.startDateKey || formatLocalISODate(selectedPayPeriod.startAt || new Date()));
    setEndDate(selectedPayPeriod.endDateKey || formatLocalISODate(selectedPayPeriod.endAt || new Date()));
  }, [dateRangeMode, selectedPayPeriod]);

  // function-scoped helper storage for resolved location timezone (used in multiple branches)
  let locationTimezone = null;

  // Recompute add break validation when add-shift break related inputs change
  useEffect(() => {
    if (!addClockInTime) {
      setAddBreakError("");
      return;
    }
    const base = new Date(`${addDate}T${addClockInTime}`);
    let prospectiveClockOut = null;
    if (addClockOutTime) {
      prospectiveClockOut = new Date(`${addDate}T${addClockOutTime}`);
      if (addClockOutTime <= addClockInTime) prospectiveClockOut.setDate(prospectiveClockOut.getDate() + 1);
    }
    const v = validateBreakWithinShift(base, prospectiveClockOut, addBreakStartTime, addBreakMinutes);
    setAddBreakError(v.valid ? "" : v.error);
  }, [addDate, addClockInTime, addClockOutTime, addBreakStartTime, addBreakMinutes]);

  // Load employees and locations
  useEffect(() => {
  const loadReferenceData = async () => {
      try {
    const tenantId = await tryResolveTenantId();
  if (!tenantId) return;

    // Load employees (filter out terminated)
    const employeesQuery = query(collection(db, `tenants/${tenantId}/employees`), orderBy("lastName"));
    const employeesSnapshot = await getDocs(employeesQuery);
        const employeesData = employeesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).filter(emp => {
          const status = emp.employment?.currentStatus;
          return !status || status !== "Terminated";
        });
        setEmployees(employeesData);

  // Load locations
  const locationsQuery = query(collection(db, `tenants/${tenantId}/locations`), orderBy("name"));
  const locationsSnapshot = await getDocs(locationsQuery);
        const locationsData = locationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setLocations(locationsData);
  // Load break types
  const breakTypesQuery = query(collection(db, `tenants/${tenantId}/breakTypes`), orderBy('name'));
  const breakTypesSnapshot = await getDocs(breakTypesQuery);
  setBreakTypes(breakTypesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

  // Load pay periods (for payroll-aligned date ranges)
  const payPeriodsQuery = query(
    collection(db, `tenants/${tenantId}/payPeriods`),
    orderBy('startDate', 'desc'),
    limit(60)
  );
  const payPeriodsSnapshot = await getDocs(payPeriodsQuery);
  const parsedPayPeriods = payPeriodsSnapshot.docs
    .map((d) => {
      const data = d.data() || {};
      const startAt = toDateObj(data.startDate);
      const endAt = toDateObj(data.endDate);
      const idMatch = String(d.id).match(/^(\d{8})_(\d{8})$/);
      const startDateKey = (idMatch && compactToIsoDate(idMatch[1])) || formatLocalISODate(startAt || new Date());
      const endDateKey = (idMatch && compactToIsoDate(idMatch[2])) || formatLocalISODate(endAt || new Date());
      return {
        id: d.id,
        ...data,
        startAt,
        endAt,
        startDateKey,
        endDateKey,
      };
    })
    .filter((p) => p.startAt && p.endAt)
    .sort((a, b) => a.startAt - b.startAt);
  setPayPeriods(parsedPayPeriods);

  if (parsedPayPeriods.length > 0) {
    const now = new Date();
    let idx = parsedPayPeriods.findIndex((p) => now >= p.startAt && now <= p.endAt);
    if (idx === -1) {
      const firstFutureIdx = parsedPayPeriods.findIndex((p) => now < p.startAt);
      idx = firstFutureIdx === -1 ? (parsedPayPeriods.length - 1) : Math.max(0, firstFutureIdx - 1);
    }
    setCurrentPayPeriodIndex(idx);
    const current = parsedPayPeriods[idx];
    if (current) {
      setStartDate(current.startDateKey);
      setEndDate(current.endDateKey);
      setDateRangeMode('current');
    }
  } else {
    setCurrentPayPeriodIndex(-1);
    setDateRangeMode('custom');
  }
      } catch (error) {
        // Surface a friendly notification instead of only logging so the UI explains permission issues
        console.warn("Error loading reference data:", error);
        setLoading(false);
        setNotification({ open: true, message: `Error loading reference data: ${error.message || error}`, severity: 'error' });
      }
    };

    loadReferenceData();
  }, []);

  // Auto-load data when filters change
  useEffect(() => {
    if (dateRangeMode === 'custom') {
      if (!startDate || !endDate) return;
      loadTimeData();
      return;
    }
    if (selectedPayPeriod) {
      loadTimeData();
    }
  }, [selectedEmployee, selectedLocation, approvalFilter, startDate, endDate, dateRangeMode, selectedPayPeriod]);

  const ENABLE_LEGACY_CLIENT_AUTOCLOCKOUT = false;

  // Legacy client-side auto clock-out (disabled; handled by backend function)
  useEffect(() => {
    if (ENABLE_LEGACY_CLIENT_AUTOCLOCKOUT) {
      autoClockoutAfter20Hours();
    }
  }, []);

  const handleEditPunch = (shift) => {
    // Helper: normalize various date/time formats (Firestore Timestamp, Date, string)
    const toDateObj = (v) => {
      if (!v) return null;
      if (v && typeof v.toDate === 'function') return v.toDate();
      if (v instanceof Date) return v;
      if (typeof v === 'string') {
        const d = new Date(v);
        return isNaN(d) ? null : d;
      }
      return null;
    };

    // Combine a base date (dateVal) with a time source (either Date/Timestamp or 'HH:MM' string)
    const combineDateAndTime = (dateVal, timeSource) => {
      const base = toDateObj(dateVal) || new Date();
      const result = new Date(base);
      if (!timeSource) return result;
      // If timeSource is a Date/Timestamp
      const ts = toDateObj(timeSource);
      if (ts) {
        result.setHours(ts.getHours(), ts.getMinutes(), ts.getSeconds() || 0, 0);
        return result;
      }
      // If timeSource is an HH:MM string
      if (typeof timeSource === 'string') {
        const m = timeSource.match(/^(\d{1,2}):(\d{2})$/);
        if (m) {
          result.setHours(Number(m[1]), Number(m[2]), 0, 0);
          return result;
        }
        // Fallback parse
        const parsed = new Date(timeSource);
        if (!isNaN(parsed)) {
          result.setHours(parsed.getHours(), parsed.getMinutes(), parsed.getSeconds() || 0, 0);
          return result;
        }
      }

  return result;
    };

    // Prefill Clock-In: prefer existing clockIn time (but force the date to the scheduled shift date),
    // else prefer scheduled shiftStartTime (HH:MM), else default to 09:00 on the shift date so date is prefilled.
    const shiftDateSource = shift.date || shift.clockIn || null;
    if (shift.clockIn) {
      const dt = combineDateAndTime(shiftDateSource, shift.clockIn);
      setEditDateTime(formatLocalDateTimeValue(dt));
    } else if (shift.shiftStartTime) {
      const dt = combineDateAndTime(shiftDateSource, shift.shiftStartTime);
      setEditDateTime(formatLocalDateTimeValue(dt));
    } else if (shift.date) {
      const dt = combineDateAndTime(shift.date, '09:00');
      setEditDateTime(formatLocalDateTimeValue(dt));
    } else {
      setEditDateTime("");
    }

    // Prefill Clock-Out: prefer existing clockOut time (force date to shift.date),
    // else prefer scheduled shiftEndTime, else leave empty (user can set if needed).
    if (shift.clockOut) {
      const dtOut = combineDateAndTime(shift.date || shift.clockOut, shift.clockOut);
      setEditClockOutDateTime(formatLocalDateTimeValue(dtOut));
    } else if (shift.shiftEndTime) {
      const dtOut = combineDateAndTime(shift.date || null, shift.shiftEndTime);
      setEditClockOutDateTime(formatLocalDateTimeValue(dtOut));
    } else {
      setEditClockOutDateTime(""); // Clear if no clock-out
    }
    
  setEditLocation(shift.locationId);
  // Prefer scheduled displayRoleId when present; always keep editRole as a string (never null)
  setEditRole(shift.displayRoleId || shift.roleId || "");
  // Populate break fields if present on the shift
  setEditBreakMinutes(shift.breakMinutes || 0);
  setEditBreakTypeId(shift.breakTypeId || "");
  setEditBreakStartTime(shift.breakStartTime || "");
    setEditDialog({ open: true, shift, type: 'both' });
  };

  // Recompute edit-break validation when edit dialog fields change
  useEffect(() => {
    if (!editDialog.open) {
      setEditBreakError("");
      return;
    }

    // Determine effective clock-in/out (use edited values if present, else the shift values)
    const shift = editDialog.shift;
    const effectiveClockIn = editDateTime ? new Date(editDateTime) : (shift?.clockIn || null);
    let effectiveClockOut = editClockOutDateTime ? new Date(editClockOutDateTime) : (shift?.clockOut || null);
    if (effectiveClockIn && effectiveClockOut && effectiveClockOut <= effectiveClockIn) {
      // assume next day
      effectiveClockOut = new Date(effectiveClockOut.getTime() + 24 * 60 * 60 * 1000);
    }

    const v = validateBreakWithinShift(effectiveClockIn, effectiveClockOut, editBreakStartTime, editBreakMinutes);
    setEditBreakError(v.valid ? "" : v.error);
  }, [editDialog.open, editDateTime, editClockOutDateTime, editBreakStartTime, editBreakMinutes, editDialog.shift]);

  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      const shift = editDialog.shift;
      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('Tenant not resolved');

      // Resolve the employee's auth UID to include on timeEntries. Prefer shift.authUid, else look up employee doc.
      // Prefer the cached employee doc for authUid when possible to avoid an extra getDoc
      let resolvedAuthUid = shift?.authUid || null;
      if (!resolvedAuthUid && shift?.employeeId) {
        const cachedEmp = employees.find(e => e.id === shift.employeeId);
        if (cachedEmp) resolvedAuthUid = cachedEmp.authUid || cachedEmp.uid || null;
        else {
          try {
            const empDoc = await getDoc(doc(db, `tenants/${tenantId}/employees`, shift.employeeId));
            if (empDoc.exists()) {
              const empData = empDoc.data();
              resolvedAuthUid = empData?.authUid || empData?.uid || null;
            }
          } catch (err) {
            // Non-fatal - we'll proceed without authUid if lookup fails
            console.warn('Could not resolve employee authUid for time entry payloads', err);
          }
        }
      }

      // Cache resolved location timezone once for this save operation (prefer cached locations)
      let resolvedLocationTimezone = null;
      let resolvedLocationData = null;
      if (editLocation) {
        const cachedLoc = locations.find(l => l.id === editLocation);
        if (cachedLoc) {
          // prefer explicit ianaTimezone or a mapped IANA value
          resolvedLocationTimezone = resolveLocationIana(cachedLoc) || (cachedLoc.timezone && String(cachedLoc.timezone).includes('/') ? cachedLoc.timezone : null);
          resolvedLocationData = cachedLoc;
        } else {
          // fallback to read location doc and map its timezone/display label to IANA
          try {
            const locDoc = await getDoc(doc(db, `tenants/${tenantId}/locations`, editLocation));
            if (locDoc.exists()) {
              resolvedLocationData = { id: editLocation, ...locDoc.data() };
              resolvedLocationTimezone = resolveLocationIana(locDoc.data()) || mapDisplayToIana(locDoc.data()?.timezone || locDoc.data()?.locationTimezone || locDoc.data()?.name);
            }
          } catch (e) {
            // ignore - we'll write null if unresolved
          }
        }
      }
      if (editLocation) {
        resolvedLocationData = await resolveLocationForBusinessDate(tenantId, editLocation);
        if (resolvedLocationData) {
          resolvedLocationTimezone = resolveLocationIana(resolvedLocationData) || resolvedLocationTimezone;
        }
      }

      // Determine effective clock-in and clock-out for validation (use edited values if provided)
      // Parse edited date/time values in the resolved location timezone when available to keep admin edits aligned to business zone.
      const effectiveClockIn = editDateTime
        ? (resolvedLocationTimezone ? DateTime.fromISO(editDateTime, { zone: resolvedLocationTimezone }).toJSDate() : new Date(editDateTime))
        : (shift.clockIn || null);
      let effectiveClockOut = editClockOutDateTime
        ? (resolvedLocationTimezone ? DateTime.fromISO(editClockOutDateTime, { zone: resolvedLocationTimezone }).toJSDate() : new Date(editClockOutDateTime))
        : (shift.clockOut || null);
      // If edited clock-out is present but before clock-in, assume next day (overnight)
      if (effectiveClockIn && effectiveClockOut && effectiveClockOut <= effectiveClockIn) {
        effectiveClockOut = new Date(effectiveClockOut.getTime() + 24 * 60 * 60 * 1000);
      }

      // Validate break values before making any updates
      const preValidation = validateBreakWithinShift(effectiveClockIn, effectiveClockOut, editBreakStartTime, editBreakMinutes);
      if (!preValidation.valid) {
        setNotification({ open: true, message: preValidation.error, severity: 'error' });
        setEditLoading(false);
        return;
      }

      // Precompute newClockIn/newClockOut timestamps (zone-aware) for subsequent create/update operations
      let newClockInTimestamp = null;
      if (editDateTime) {
        newClockInTimestamp = resolvedLocationTimezone ? DateTime.fromISO(editDateTime, { zone: resolvedLocationTimezone }).toJSDate() : new Date(editDateTime);
      } else {
        newClockInTimestamp = shift.clockIn || null;
      }

      // Precompute clock-out as well (used later)
      let newClockOutTimestamp = null;
      if (editClockOutDateTime) {
        newClockOutTimestamp = resolvedLocationTimezone ? DateTime.fromISO(editClockOutDateTime, { zone: resolvedLocationTimezone }).toJSDate() : new Date(editClockOutDateTime);
      } else {
        newClockOutTimestamp = shift.clockOut || null;
      }
      const getEditBusinessDate = (ts) => getTimeEntryBusinessDate(
        resolvedLocationData,
        ts,
        resolvedLocationTimezone
      );

      // Update Clock In time
      if (editDateTime) {

        const clockInQuery = query(
          collection(db, `tenants/${tenantId}/timeEntries`),
          where("employeeId", "==", shift.employeeId),
          where("type", "==", "clock_in")
        );

        const clockInSnapshot = await getDocs(clockInQuery);
        const clockInEntry = clockInSnapshot.docs.find(doc => {
          const data = doc.data();
          const entryTime = data.timestamp.toDate();
          return Math.abs(entryTime - shift.clockIn) < 60000; // Within 1 minute
        });

        if (clockInEntry) {
          // Validate break relative to edited clock-in and any clock-out
          const existingClockOut = editClockOutDateTime ? new Date(editClockOutDateTime) : (shift.clockOut || null);
          const v = validateBreakWithinShift(newClockInTimestamp, existingClockOut, editBreakStartTime, editBreakMinutes);
          if (!v.valid) {
            setNotification({ open: true, message: v.error, severity: 'error' });
            setEditLoading(false);
            return;
          }

          // attempt to find a matching scheduled shift and attach shiftId
          try {
            // If we're editing a known scheduled shift, prefer to attach its id directly
            // to avoid relying on the matching heuristic which may fail in edge cases.
            // Still keep the matching call for parity with other flows.
            let match = null;
            try {
              match = await findMatchingShift(tenantId, { employeeId: shift.employeeId, timestamp: Timestamp.fromDate(newClockInTimestamp), date: getEditBusinessDate(newClockInTimestamp), locationId: editLocation, roleId: editRole });
            } catch (e) {
              // non-fatal
            }

            const payload = {
              timestamp: Timestamp.fromDate(newClockInTimestamp),
              locationId: editLocation,
              locationTimezone: resolvedLocationTimezone,
              roleId: editRole,
              date: getEditBusinessDate(newClockInTimestamp),
              breakMinutes: editBreakMinutes > 0 ? editBreakMinutes : 0,
              breakTypeId: editBreakTypeId || null,
              breakStartTime: editBreakStartTime || null
            };
            // Prefer the explicit shift being edited when available
            if (shift && shift.id) payload.shiftId = shift.id;
            else if (match && match.id) payload.shiftId = match.id;
            await updateDoc(doc(db, `tenants/${tenantId}/timeEntries`, clockInEntry.id), payload);
          } catch (err) {
            await updateDoc(doc(db, `tenants/${tenantId}/timeEntries`, clockInEntry.id), {
              timestamp: Timestamp.fromDate(newClockInTimestamp),
              locationId: editLocation,
              roleId: editRole,
              date: getEditBusinessDate(newClockInTimestamp),
              breakMinutes: editBreakMinutes > 0 ? editBreakMinutes : 0,
              breakTypeId: editBreakTypeId || null,
              breakStartTime: editBreakStartTime || null
            });
          }
        } else {
          // No existing clock-in entry found; create one
          try {
            const payload = {
              employeeId: shift.employeeId,
              employeeName: shift.employeeName || getEmployeeName(shift.employeeId),
              type: 'clock_in',
              timestamp: Timestamp.fromDate(newClockInTimestamp),
              locationId: editLocation,
              locationTimezone: resolvedLocationTimezone,
              roleId: editRole,
              date: getEditBusinessDate(newClockInTimestamp),
              businessDateKey: getEditBusinessDate(newClockInTimestamp),
              breakMinutes: editBreakMinutes > 0 ? editBreakMinutes : 0,
              breakTypeId: editBreakTypeId || null,
              breakStartTime: editBreakStartTime || null,
              authUid: resolvedAuthUid || null
            };
            let match = null;
            try {
              match = await findMatchingShift(tenantId, { employeeId: shift.employeeId, timestamp: Timestamp.fromDate(newClockInTimestamp), date: getEditBusinessDate(newClockInTimestamp), locationId: editLocation, roleId: editRole });
            } catch (e) {
              // non-fatal
            }
            if (shift && shift.id) payload.shiftId = shift.id;
            else if (match && match.id) payload.shiftId = match.id;
            const ref = await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), payload);
            if (payload.shiftId) {
              await updateShiftTimecard(tenantId, payload.shiftId);
            }
          } catch (err) {
            const fallback = {
              employeeId: shift.employeeId,
              employeeName: shift.employeeName || getEmployeeName(shift.employeeId),
              type: 'clock_in',
              timestamp: Timestamp.fromDate(newClockInTimestamp),
              locationId: editLocation,
              locationTimezone: resolvedLocationTimezone,
              roleId: editRole,
              date: getEditBusinessDate(newClockInTimestamp),
              businessDateKey: getEditBusinessDate(newClockInTimestamp),
              breakMinutes: editBreakMinutes > 0 ? editBreakMinutes : 0,
              breakTypeId: editBreakTypeId || null,
              breakStartTime: editBreakStartTime || null,
              authUid: resolvedAuthUid || null
            };
            // Attach explicit shift id if available
            if (shift && shift.id) fallback.shiftId = shift.id;
            const ref = await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), fallback);
            if (fallback.shiftId) {
              await updateShiftTimecard(tenantId, fallback.shiftId);
            }
          }
        }
      }

      // Update Clock Out time (if provided)
      if (editClockOutDateTime && shift.clockOut) {
        const updatedClockOutTimestamp = newClockOutTimestamp;
        
        // Find the clock-out entry
        const clockOutQuery = query(
          collection(db, `tenants/${tenantId}/timeEntries`),
          where("employeeId", "==", shift.employeeId),
          where("type", "==", "clock_out")
        );
        
        const clockOutSnapshot = await getDocs(clockOutQuery);
        const clockOutEntry = clockOutSnapshot.docs.find(doc => {
          const data = doc.data();
          const entryTime = data.timestamp.toDate();
          return Math.abs(entryTime - shift.clockOut) < 60000; // Within 1 minute
        });

        if (clockOutEntry) {
          // Validate break relative to clock-in and edited clock-out
          // (we already computed newClockInTimestamp/newClockOutTimestamp above)
          const v2 = validateBreakWithinShift(newClockInTimestamp, newClockOutTimestamp, editBreakStartTime, editBreakMinutes);
          if (!v2.valid) {
            setNotification({ open: true, message: v2.error, severity: 'error' });
            setEditLoading(false);
            return;
          }

          try {
            let match = null;
            try {
              match = await findMatchingShift(tenantId, { employeeId: shift.employeeId, timestamp: Timestamp.fromDate(updatedClockOutTimestamp), date: getEditBusinessDate(updatedClockOutTimestamp), locationId: editLocation, roleId: editRole });
            } catch (e) { /* non-fatal */ }
            const payload = {
              timestamp: Timestamp.fromDate(updatedClockOutTimestamp),
              locationId: editLocation,
              locationTimezone: resolvedLocationTimezone,
              roleId: editRole,
              date: getEditBusinessDate(updatedClockOutTimestamp),
              breakMinutes: editBreakMinutes > 0 ? editBreakMinutes : 0,
              breakTypeId: editBreakTypeId || null,
              breakStartTime: editBreakStartTime || null
            };
            // Prefer the explicit shift being edited when available
            if (shift && shift.id) payload.shiftId = shift.id;
            else if (match && match.id) payload.shiftId = match.id;
            await updateDoc(doc(db, `tenants/${tenantId}/timeEntries`, clockOutEntry.id), payload);
          } catch (err) {
            const updateFallback = {
              timestamp: Timestamp.fromDate(updatedClockOutTimestamp),
              locationId: editLocation,
              locationTimezone: resolvedLocationTimezone,
              roleId: editRole,
              date: getEditBusinessDate(updatedClockOutTimestamp),
              breakMinutes: editBreakMinutes > 0 ? editBreakMinutes : 0,
              breakTypeId: editBreakTypeId || null,
              breakStartTime: editBreakStartTime || null
            };
            if (shift && shift.id) updateFallback.shiftId = shift.id;
            await updateDoc(doc(db, `tenants/${tenantId}/timeEntries`, clockOutEntry.id), updateFallback);
          }
        }
      }
      // Create clock-out entry if it doesn't exist but time is provided
      else if (editClockOutDateTime && !shift.clockOut) {
        // Use precomputed zone-aware timestamps
        // newClockOutTimestamp and newClockInTimestamp were computed earlier
        const v3 = validateBreakWithinShift(newClockInTimestamp, newClockOutTimestamp, editBreakStartTime, editBreakMinutes);
        if (!v3.valid) {
          setNotification({ open: true, message: v3.error, severity: 'error' });
          setEditLoading(false);
          return;
        }

          try {
            const payload = {
              employeeId: shift.employeeId,
              employeeName: shift.employeeName,
              type: "clock_out",
              timestamp: Timestamp.fromDate(newClockOutTimestamp),
              locationId: editLocation,
              locationTimezone: resolvedLocationTimezone,
              roleId: editRole,
              date: getEditBusinessDate(newClockOutTimestamp),
              businessDateKey: getEditBusinessDate(newClockOutTimestamp),
              breakMinutes: editBreakMinutes > 0 ? editBreakMinutes : 0,
              breakTypeId: editBreakTypeId || null,
              breakStartTime: editBreakStartTime || null,
              authUid: resolvedAuthUid || null
            };
            let match = null;
            try {
              match = await findMatchingShift(tenantId, { employeeId: shift.employeeId, timestamp: Timestamp.fromDate(newClockOutTimestamp), date: getEditBusinessDate(newClockOutTimestamp), locationId: editLocation, roleId: editRole });
            } catch (e) { /* non-fatal */ }
            if (shift && shift.id) payload.shiftId = shift.id;
            else if (match && match.id) payload.shiftId = match.id;
            const ref = await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), payload);
            if (payload.shiftId) {
              await updateShiftTimecard(tenantId, payload.shiftId);
            }
          } catch (err) {
            const fallback = {
              employeeId: shift.employeeId,
              employeeName: shift.employeeName,
              type: "clock_out",
              timestamp: Timestamp.fromDate(newClockOutTimestamp),
              locationId: editLocation,
              locationTimezone: resolvedLocationTimezone,
              roleId: editRole,
              date: getEditBusinessDate(newClockOutTimestamp),
              businessDateKey: getEditBusinessDate(newClockOutTimestamp),
              breakMinutes: editBreakMinutes > 0 ? editBreakMinutes : 0,
              breakTypeId: editBreakTypeId || null,
              breakStartTime: editBreakStartTime || null,
              authUid: resolvedAuthUid || null
            };
            const ref = await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), fallback);
            if (fallback.shiftId) {
              await updateShiftTimecard(tenantId, fallback.shiftId);
            }
          }
      }

      setNotification({
        open: true,
        message: "Time entries updated successfully",
        severity: "success"
      });
      
      // Reload data
      loadTimeData();
    } catch (error) {
      console.error("Error updating time entries:", error);
      setNotification({
        open: true,
        message: "Error updating time entries",
        severity: "error"
      });
    } finally {
      setEditLoading(false);
      setEditDialog({ open: false, shift: null, type: null });
    }
  };

  const handleDeleteShift = async () => {
    setEditLoading(true);
    try {
      // Find and delete both clock-in and clock-out entries for this shift
      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('Tenant not resolved');

      const timeEntriesQuery = query(
        collection(db, `tenants/${tenantId}/timeEntries`),
        where("employeeId", "==", deleteDialog.shift.employeeId)
      );
      
      const snapshot = await getDocs(timeEntriesQuery);
      const entriesToDelete = snapshot.docs.filter(doc => {
        const data = doc.data();
        const entryTime = data.timestamp.toDate();
        const shiftDate = deleteDialog.shift.date.toDateString();
        return entryTime.toDateString() === shiftDate;
      });

      for (const entry of entriesToDelete) {
        // Mark entry as voided instead of deleting
        try {
          const user = auth.currentUser || null;
          const voidedByUid = user ? user.uid : null;
          const voidedAt = Timestamp.fromDate(new Date());
          await updateDoc(doc(db, `tenants/${tenantId}/timeEntries`, entry.id), {
            status: 'voided',
            voidedAt,
            voidedByUid,
            voidReason: 'Deleted via timesheet'
          });
        } catch (err) {
          // Do not delete the entry; log a warning so the issue can be investigated.
          console.warn('Failed to mark time entry as voided, entry id:', entry.id, err);
        }
      }

      setNotification({
        open: true,
        message: "Shift deleted successfully",
        severity: "success"
      });
      
      // Reload data
      loadTimeData();
    } catch (error) {
      console.error("Error deleting shift:", error);
      setNotification({
        open: true,
        message: "Error deleting shift",
        severity: "error"
      });
    } finally {
      setEditLoading(false);
      setDeleteDialog({ open: false, shift: null });
    }
  };

  const handleAddShift = async () => {
    setEditLoading(true);
    try {
  // eslint-disable-next-line no-console
  if (process.env.NODE_ENV !== 'production') console.info('handleAddShift called for employee', addEmployee);
      if (!addEmployee || !addDate || !addClockInTime || !addLocationId || !addRoleId) {
        setNotification({
          open: true,
          message: "Please fill in all required fields",
          severity: "error"
        });
        setEditLoading(false);
        return;
      }

      // Validate shift times using business day logic
      if (addClockOutTime) {
        // pass location-specific business day cutoff if available
        const selectedLoc = addLocationId ? locations.find((l) => l.id === addLocationId) : null;
        const validation = await validateShiftTime(addClockInTime, addClockOutTime, { endTimeOverride: resolveLocationBusinessDayCutoff(selectedLoc) });
        if (!validation.valid) {
          setNotification({
            open: true,
            message: validation.error,
            severity: "error"
          });
          setEditLoading(false);
          return;
        }
      }

      const employee = employees.find(emp => emp.id === addEmployee);
      if (!employee) {
        setNotification({
          open: true,
          message: "Selected employee not found",
          severity: "error"
        });
        setEditLoading(false);
        return;
      }

      // Create entries for selected weekdays (if any) or just the single addDate
      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('Tenant not resolved');
        // Create clock-in entry
        // Resolve timezone for add flow
        let addResolvedLocationTimezone = null;
        let addLocationData = null;
        if (addLocationId) {
          const cachedAddLoc = locations.find(l => l.id === addLocationId);
          if (cachedAddLoc) {
            addLocationData = cachedAddLoc;
            addResolvedLocationTimezone = resolveLocationIana(cachedAddLoc) || (cachedAddLoc.timezone && String(cachedAddLoc.timezone).includes('/') ? cachedAddLoc.timezone : null);
          }
          else {
            try {
              const locDoc = await getDoc(doc(db, `tenants/${tenantId}/locations`, addLocationId));
              if (locDoc.exists()) {
                addLocationData = { id: addLocationId, ...locDoc.data() };
                addResolvedLocationTimezone = resolveLocationIana(locDoc.data()) || mapDisplayToIana(locDoc.data()?.timezone || locDoc.data()?.locationTimezone || locDoc.data()?.name);
              }
            } catch (e) { /* ignore */ }
          }
        }
        if (addLocationId) {
          addLocationData = await resolveLocationForBusinessDate(tenantId, addLocationId);
          if (addLocationData) {
            addResolvedLocationTimezone = resolveLocationIana(addLocationData) || addResolvedLocationTimezone;
          }
        }

        const clockInDateTime = addResolvedLocationTimezone ? DateTime.fromISO(`${addDate}T${addClockInTime}`, { zone: addResolvedLocationTimezone }).toJSDate() : new Date(`${addDate}T${addClockInTime}Z`);
      // final safety check (should already be validated inline) - compute prospectiveClockOut
      let prospectiveClockOut = null;
      if (addClockOutTime) {
        prospectiveClockOut = addResolvedLocationTimezone ? DateTime.fromISO(`${addDate}T${addClockOutTime}`, { zone: addResolvedLocationTimezone }).toJSDate() : new Date(`${addDate}T${addClockOutTime}Z`);
        if (addClockOutTime <= addClockInTime) {
          prospectiveClockOut.setDate(prospectiveClockOut.getDate() + 1);
        }
      }
      const validation = validateBreakWithinShift(clockInDateTime, prospectiveClockOut, addBreakStartTime, addBreakMinutes);
      if (!validation.valid) {
        setNotification({ open: true, message: validation.error, severity: 'error' });
        setEditLoading(false);
        return;
      }

      let clockInRef = null;
  let clockOutRef = null;
          const clockInBusinessDate = getTimeEntryBusinessDate(addLocationData, clockInDateTime, addResolvedLocationTimezone);
          // Build payload and attempt to find matching scheduled shift
          const clockInPayload = {
            employeeId: addEmployee,
            employeeName: `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
            type: "clock_in",
            timestamp: Timestamp.fromDate(clockInDateTime),
            locationId: addLocationId,
            roleId: addRoleId,
            date: clockInBusinessDate,
            businessDateKey: clockInBusinessDate,
            breakMinutes: addBreakMinutes > 0 ? addBreakMinutes : 0,
            breakTypeId: addBreakTypeId || null,
            breakStartTime: addBreakStartTime || null,
            // include employee auth UID when available on employee doc
            authUid: employee.authUid || employee.uid || null
          };
          try {
            // Match using the zone-aware clockIn timestamp used above
            const match = await findMatchingShift(tenantId, { employeeId: addEmployee, timestamp: Timestamp.fromDate(clockInDateTime), date: clockInBusinessDate, locationId: addLocationId, roleId: addRoleId });
            if (match && match.id) clockInPayload.shiftId = match.id;
          } catch (err) {
            // non-fatal
          }
          clockInRef = await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), clockInPayload);
          try {
            if (clockInPayload.shiftId) {
              await updateShiftTimecard(tenantId, clockInPayload.shiftId);
            }
          } catch (err) {
            console.warn('Failed to mirror add-shift clock-in', err);
          }
  // eslint-disable-next-line no-console
  if (process.env.NODE_ENV !== 'production') console.info('clock-in written', clockInRef.id);

        // Create clock-out entry if provided
          if (addClockOutTime) {
            let clockOutDateTime = addResolvedLocationTimezone ? DateTime.fromISO(`${addDate}T${addClockOutTime}`, { zone: addResolvedLocationTimezone }).toJSDate() : new Date(`${addDate}T${addClockOutTime}Z`);

          // Handle overnight shifts - if clock-out time is before clock-in time, it's next day
          if (addClockOutTime <= addClockInTime) {
            clockOutDateTime.setDate(clockOutDateTime.getDate() + 1);
          }
        
          const clockOutBusinessDate = getTimeEntryBusinessDate(addLocationData, clockOutDateTime, addResolvedLocationTimezone);
          const clockOutPayload = {
            employeeId: addEmployee,
            employeeName: `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
            type: "clock_out",
            timestamp: Timestamp.fromDate(clockOutDateTime),
            locationId: addLocationId,
            roleId: addRoleId,
            date: clockOutBusinessDate,
            businessDateKey: clockOutBusinessDate,
            breakMinutes: addBreakMinutes > 0 ? addBreakMinutes : 0,
            breakTypeId: addBreakTypeId || null,
            breakStartTime: addBreakStartTime || null,
            authUid: employee.authUid || employee.uid || null
          };
            try {
            const matchOut = await findMatchingShift(tenantId, { employeeId: addEmployee, timestamp: Timestamp.fromDate(clockOutDateTime), date: clockOutBusinessDate, locationId: addLocationId, roleId: addRoleId });
            if (matchOut && matchOut.id) clockOutPayload.shiftId = matchOut.id;
          } catch (err) {
            // non-fatal
          }
          clockOutRef = await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), clockOutPayload);
          try {
            if (clockOutPayload.shiftId) {
              await updateShiftTimecard(tenantId, clockOutPayload.shiftId);
            }
          } catch (err) {
            console.warn('Failed to mirror add-shift clock-out', err);
          }
          // eslint-disable-next-line no-console
          if (process.env.NODE_ENV !== 'production') console.info('clock-out written', clockOutRef.id);
          // Mirror newly created entries if possible (attach shiftId if matching found previously is handled earlier in this function)
          try {
            // If we created entries with shiftId earlier in this flow, mirror them
            // Note: add-shift flow currently doesn't compute shiftId; leave mirroring for flows that set shiftId on payload.
          } catch (err) {
            console.warn('Mirroring add-shift entries skipped or failed', err);
          }
        }

      setNotification({
        open: true,
        message: addClockOutTime ? `Shift added successfully (in: ${clockInRef?.id}, out: ${clockOutRef?.id || 'n/a'})` : `Clock-in entry added successfully (id: ${clockInRef?.id})`,
        severity: "success"
      });

      // Reset form
      setAddEmployee("");
  setAddDate(formatLocalISODate());
      setAddClockInTime("");
      setAddClockOutTime("");
      setAddLocationId("");
      setAddRoleId("");
  setAddBreakMinutes(0);
  setAddBreakQuick(0);
  setAddBreakTypeId("");
  setAddBreakStartTime("");
  setAddShiftDialog({ open: false });
  setSelectedWeekdays([]);
      
      // Reload data
      loadTimeData();
        } catch (error) {
          console.error("Error adding shift:", error);
          setNotification({
            open: true,
            // Surface the actual error message to help debugging (kept short)
            message: error?.message ? `Error adding shift: ${error.message}` : "Error adding shift",
            severity: "error"
          });
        } finally {
          setEditLoading(false);
        }
  };

  // Get roles assigned to the selected employee
  const getEmployeeRoles = () => {
    if (!addEmployee || !employees.length || !roles.length) {
      return [];
    }

  const employee = employees.find(emp => emp.id === addEmployee);
  // Prefer flattened reconstructed nested assignments from hook (hook called at top-level), falling back to employee.assignments
  const effectiveAssignments = (flattenedNestedForAddEmployee && Object.keys(flattenedNestedForAddEmployee).length) ? flattenedNestedForAddEmployee : (employee && employee.assignments ? employee.assignments : {});

    const assignedRoleIds = new Set();
    Object.values(effectiveAssignments).forEach(locationAssignment => {
      if (locationAssignment.departments) {
        Object.values(locationAssignment.departments).forEach(deptAssignment => {
          if (deptAssignment.roles) {
            if (Array.isArray(deptAssignment.roles)) {
              deptAssignment.roles.forEach(roleId => assignedRoleIds.add(roleId));
            } else {
              Object.entries(deptAssignment.roles).forEach(([roleId, roleData]) => {
                if (roleData && roleData.assigned) assignedRoleIds.add(roleId);
              });
            }
          }
        });
      }
    });

    return roles.filter(role => assignedRoleIds.has(role.id));
  };

  const autoClockoutAfter20Hours = async () => {
    try {
      const now = new Date();
      const twentyHoursAgo = new Date(now.getTime() - 20 * 60 * 60 * 1000);
      
      const tenantId = await tryResolveTenantId();
      if (!tenantId) return;

      // Query only by timestamp to avoid requiring a composite index in Firestore.
      const timeEntriesQuery = query(
        collection(db, `tenants/${tenantId}/timeEntries`),
        where("timestamp", "<=", Timestamp.fromDate(twentyHoursAgo)),
        orderBy("timestamp", "asc")
      );

      const snapshot = await getDocs(timeEntriesQuery);
      const locationCache = new Map();
      const getLocationForEntry = async (entryLocationId) => {
        if (!entryLocationId) return null;
        if (locationCache.has(entryLocationId)) return locationCache.get(entryLocationId);
        const cached = locations.find((l) => l.id === entryLocationId) || null;
        if (cached?.businessDayCutoff) {
          locationCache.set(entryLocationId, cached);
          return cached;
        }
        try {
          const locDoc = await getDoc(doc(db, `tenants/${tenantId}/locations`, entryLocationId));
          if (locDoc.exists()) {
            const data = { id: entryLocationId, ...locDoc.data() };
            locationCache.set(entryLocationId, data);
            return data;
          }
        } catch (e) {
          // ignore
        }
        locationCache.set(entryLocationId, cached);
        return cached;
      };
      
      for (const docSnap of snapshot.docs) {
        const entry = docSnap.data();
        // Skip voided entries
        if (entry && entry.status === 'voided') continue;
        // Only handle clock-in entries
        if (entry.type !== 'clock_in') continue;

        // Check if there's already a clock-out for this employee after this clock-in
        const clockOutQuery = query(
          collection(db, `tenants/${tenantId}/timeEntries`),
          where("employeeId", "==", entry.employeeId),
          where("type", "==", "clock_out"),
          where("timestamp", ">", entry.timestamp)
        );

        const clockOutSnapshot = await getDocs(clockOutQuery);
        // Exclude voided clock-out entries so voided docs don't block auto clock-out
        const clockOutEntries = clockOutSnapshot.docs.map(d => d.data()).filter(e => e.status !== 'voided');

        if (clockOutEntries.length === 0) {
          // No clock-out found, create auto clock-out after 20 hours
          const autoClockOutTime = new Date(entry.timestamp.toDate().getTime() + 20 * 60 * 60 * 1000);
          const locationData = await getLocationForEntry(entry.locationId);
          
          const payload = {
            employeeId: entry.employeeId,
            employeeName: entry.employeeName,
            type: "clock_out",
            timestamp: Timestamp.fromDate(autoClockOutTime),
            locationId: entry.locationId,
            roleId: entry.roleId,
            date: getTimeEntryBusinessDate(locationData, autoClockOutTime, entry.locationTimezone),
            businessDateKey: getTimeEntryBusinessDate(locationData, autoClockOutTime, entry.locationTimezone),
            isAutoClockOut: true
          };
          const ref = await addDoc(collection(db, `tenants/${tenantId}/timeEntries`), payload);
          try {
            if (payload.shiftId) {
              await updateShiftTimecard(tenantId, payload.shiftId);
            }
          } catch (err) {
            console.warn('Failed to mirror auto clock-out (new)', err);
          }
        }
      }
    } catch (error) {
      console.error("Error with auto clock-out:", error);
    }
  };

  const loadTimeData = async () => {
    setLoading(true);
    try {
  let start;
  let end;
  let startDateKey = startDate;
  let endDateKey = endDate;
  if (dateRangeMode !== 'custom' && selectedPayPeriod?.startAt && selectedPayPeriod?.endAt) {
    // Payroll-aligned ranges use exact period boundaries (timestamps from payPeriods docs)
    start = new Date(selectedPayPeriod.startAt);
    end = new Date(selectedPayPeriod.endAt);
    startDateKey = selectedPayPeriod.startDateKey || startDate;
    endDateKey = selectedPayPeriod.endDateKey || endDate;
  } else {
    // Custom range continues using local date boundaries
    const [sy, sm, sd] = startDate.split('-').map(Number);
    const [ey, em, ed] = endDate.split('-').map(Number);
    start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
    end = new Date(ey, em - 1, ed, 23, 59, 59, 999);
  }

      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('tenantId not resolved');

      let timeEntriesQuery = query(
        collection(db, `tenants/${tenantId}/timeEntries`),
        where("timestamp", ">=", Timestamp.fromDate(start)),
        where("timestamp", "<=", Timestamp.fromDate(end)),
        orderBy("timestamp", "desc")
      );

  const snapshot = await getDocs(timeEntriesQuery);
  // Exclude voided time entries so they do not appear in the timesheet
  const timeEntries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(e => e.status !== 'voided');

      // Also load scheduled shifts for this date range so we can show scheduled context
      let scheduledShifts = [];
      try {
  const weekStartKey = startDateKey || require('../utils/shiftDate').toIsoDateString(start);
  const endKey = endDateKey || require('../utils/shiftDate').toIsoDateString(end);
        const shiftsQ = query(
            collection(db, `tenants/${tenantId}/shifts`),
            where('shiftDateKey', '>=', weekStartKey),
            where('shiftDateKey', '<=', endKey),
            orderBy('shiftDateKey')
          );
        const shiftsSnap = await getDocs(shiftsQ);
        // Exclude shifts that are marked deleted in Firestore
    scheduledShifts = shiftsSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => !s.deleted);
      } catch (e) {
        // non-fatal: if shifts can't be loaded we'll still show raw time entries
        console.warn('Could not load shifts for timesheet:', e);
      }

      const timecardQuery = query(
        collection(db, `tenants/${tenantId}/timecards`),
        where("businessDateKey", ">=", startDateKey),
        where("businessDateKey", "<=", endDateKey),
        orderBy("businessDateKey", "desc"),
      );
      const timecardSnap = await getDocs(timecardQuery);
      const timecards = timecardSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Group time entries into shifts, preferring scheduled shifts when available
      const shifts = groupTimeEntriesIntoShifts(timeEntries, scheduledShifts, timecards);
      setShiftData(shifts);
      
      // Calculate total hours
      const total = shifts.reduce((sum, shift) => sum + (shift.hours || 0), 0);
      setTotalHours(total);
      
    } catch (error) {
      console.error("Error loading time data:", error);
    } finally {
      setLoading(false);
    }
  };

  const groupTimeEntriesIntoShifts = (timeEntries, scheduledShifts = [], timecards = []) => {
    const shifts = [];
    const toNumber = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    };
    const getTakenBreakMinutesFromEntries = (entries = []) => {
      if (!entries.length) return 0;
      return entries.reduce((max, e) => Math.max(max, toNumber(e?.breakMinutes)), 0);
    };
    const getScheduledBreakMinutes = (scheduledShift) => {
      if (!scheduledShift) return 0;
      const breaks = scheduledShift.breaks;
      if (Array.isArray(breaks)) {
        return breaks.reduce((sum, b) => sum + Math.max(0, toNumber(b?.duration ?? b?.minutes)), 0);
      }
      if (breaks && typeof breaks === 'object') {
        return Object.values(breaks).reduce(
          (sum, b) => sum + Math.max(0, toNumber(b?.duration ?? b?.minutes)),
          0
        );
      }
      return Math.max(0, toNumber(scheduledShift.breakMinutes));
    };
    const toDateKey = (d) => {
      if (!d) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const timecardByShiftId = new Map();
    const timecardByEmployeeDate = new Map();
    timecards.forEach((tc) => {
      if (tc?.shiftId) timecardByShiftId.set(tc.shiftId, tc);
      const k = `${tc.employeeId || ''}-${tc.businessDateKey || tc.localDateKey || ''}`;
      if (tc?.employeeId && (tc?.businessDateKey || tc?.localDateKey) && !timecardByEmployeeDate.has(k)) {
        timecardByEmployeeDate.set(k, tc);
      }
    });

    // Index time entries by employeeId + date (local date string)
    const entriesIndex = {};
    timeEntries.forEach(entry => {
      // Skip voided entries defensively here as well
      if (entry && entry.status === 'voided') return;
      const employeeId = entry.employeeId;
      const dateStr = entry.timestamp?.toDate ? entry.timestamp.toDate().toDateString() : new Date(entry.timestamp).toDateString();
      const key = `${employeeId}-${dateStr}`;
      if (!entriesIndex[key]) entriesIndex[key] = [];
      entriesIndex[key].push(entry);
    });

    // First, prefer scheduled shifts: create rows for each scheduled shift and attach any matching time entries
    scheduledShifts.forEach(s => {
      // Defensive: skip deleted shifts if any slipped through
      if (s.deleted) return;
  const { toDate } = require('../utils/shiftDate');
  const shiftDate = s.shiftDateKey ? toDate(s.shiftDateKey) : toDate(s.shiftDate);
      const key = `${s.employeeId}-${shiftDate.toDateString()}`;
      const entries = entriesIndex[key] || [];

      // Find clock-ins and clock-outs among entries
      const clockIns = entries.filter(e => e.type === 'clock_in').sort((a, b) => a.timestamp.toDate() - b.timestamp.toDate());
      const clockOuts = entries.filter(e => e.type === 'clock_out').sort((a, b) => a.timestamp.toDate() - b.timestamp.toDate());

      const clockIn = clockIns[0] ? clockIns[0].timestamp.toDate() : null;
      const clockOut = clockOuts.find(out => !clockIn || out.timestamp.toDate() > clockIn) ? (clockOuts.find(out => !clockIn || out.timestamp.toDate() > clockIn)?.timestamp.toDate()) : null;
      const takenBreakMinutes = getTakenBreakMinutesFromEntries(entries);
      const scheduledBreakMinutes = getScheduledBreakMinutes(s);

      const businessDateKey = s.shiftDateKey || toDateKey(shiftDate);
      const matchedTimecard =
        timecardByShiftId.get(s.id) ||
        timecardByEmployeeDate.get(`${s.employeeId || ''}-${businessDateKey || ''}`) ||
        null;

      const shift = {
        id: s.id || `${s.employeeId}-${shiftDate.getTime()}`,
        employeeId: s.employeeId,
        employeeName: s.employeeName || getEmployeeName(s.employeeId),
        date: shiftDate,
  locationId: s.locationId || (entries[0] && entries[0].locationId) || null,
  roleId: s.roleId || (entries[0] && entries[0].roleId) || null,
  // Prefer a human-friendly role id/name from the scheduled shift if available
  displayRoleId: s.roleId || s.role || (entries[0] && entries[0].roleId) || null,
  displayRoleName: s.roleName || s.roleName || s.roleName || null,
  // Preserve scheduled start/end times if present on the shift document
  shiftStartTime: s.shiftStartTime || s.scheduledStartTime || s.startTime || null,
  shiftEndTime: s.shiftEndTime || s.scheduledEndTime || s.endTime || null,
        clockIn: clockIn,
        clockOut: clockOut,
  isAutoClockOut: entries.find(e => e.isAutoClockOut) ? true : false,
  // Only mark missing clock-out when there was a clock-in but no clock-out
  isMissingClockOut: !!clockIn && !clockOut,
        hours: clockIn && clockOut ? calculateHours(clockIn, clockOut) : 0,
        breakMinutes: takenBreakMinutes,
        takenBreakMinutes,
        scheduledBreakMinutes,
        breakTypeId: (entries.find(e => e.breakTypeId)?.breakTypeId) || null,
        breakStartTime: (entries.find(e => e.breakStartTime)?.breakStartTime) || null,
        wage: s.wage || 14.00,
        scheduled: true,
        timecardId: matchedTimecard?.id || null,
        approvalState: matchedTimecard?.approved ? "approved" : "needs_review",
        approvalSource: matchedTimecard?.approvedBy === "system" ? "system" : (matchedTimecard?.approved ? "manager" : null),
        approvalBy: matchedTimecard?.approvedBy || null,
        hasExceptions: matchedTimecard?.hasExceptions === true || ((matchedTimecard?.exceptionFlags || []).length > 0),
        exceptionFlags: matchedTimecard?.exceptionFlags || [],
        locked: matchedTimecard?.locked === true || matchedTimecard?.payrollLocked === true,
      };

      // Apply filters
      if (selectedEmployee !== "all" && shift.employeeId !== selectedEmployee) return;
      if (selectedLocation !== "all" && shift.locationId !== selectedLocation) return;
      if (approvalFilter === "needs_review" && shift.approvalState !== "needs_review") return;

      shifts.push(shift);

      // Remove used entries from index so they aren't duplicated later
      delete entriesIndex[key];
    });

    // Next, any remaining time entry groups that didn't match a scheduled shift become standalone rows
    Object.entries(entriesIndex).forEach(([key, entries]) => {
      if (!entries || entries.length === 0) return;
      const [employeeId, ...dateParts] = key.split('-');
      const dateStr = dateParts.join('-');
      const date = entries[0].timestamp.toDate();

      const clockIns = entries.filter(e => e.type === 'clock_in').sort((a, b) => a.timestamp.toDate() - b.timestamp.toDate());
      const clockOuts = entries.filter(e => e.type === 'clock_out').sort((a, b) => a.timestamp.toDate() - b.timestamp.toDate());

      for (let i = 0; i < clockIns.length; i++) {
        const clockInEntry = clockIns[i];
        const clockOutEntry = clockOuts.find(out => out.timestamp.toDate() > clockInEntry.timestamp.toDate());
        const clockIn = clockInEntry.timestamp.toDate();
        const clockOut = clockOutEntry ? clockOutEntry.timestamp.toDate() : null;
        const takenBreakMinutes = Math.max(
          0,
          toNumber(clockOutEntry?.breakMinutes),
          toNumber(clockInEntry?.breakMinutes)
        );

        const businessDateKey = clockInEntry.businessDateKey || clockInEntry.date || toDateKey(clockIn);
        const matchedTimecard =
          (clockInEntry?.shiftId ? timecardByShiftId.get(clockInEntry.shiftId) : null) ||
          timecardByEmployeeDate.get(`${employeeId || ''}-${businessDateKey || ''}`) ||
          null;

        const shift = {
          id: `${employeeId}-${clockIn.getTime()}`,
          employeeId,
          employeeName: clockInEntry.employeeName || getEmployeeName(employeeId),
          date: date,
          locationId: clockInEntry.locationId,
          roleId: clockInEntry.roleId,
          clockIn,
          clockOut,
          isAutoClockOut: clockOutEntry?.isAutoClockOut || false,
          // Only missing when there was a clock-in but no corresponding clock-out
          isMissingClockOut: !!clockIn && !clockOut,
          hours: clockOut ? calculateHours(clockIn, clockOut) : 0,
          breakMinutes: takenBreakMinutes,
          takenBreakMinutes,
          scheduledBreakMinutes: 0,
          breakTypeId: (clockOutEntry && clockOutEntry.breakTypeId) || (clockInEntry && clockInEntry.breakTypeId) || null,
          breakStartTime: (clockOutEntry && clockOutEntry.breakStartTime) || (clockInEntry && clockInEntry.breakStartTime) || null,
          wage: 14.00,
          timecardId: matchedTimecard?.id || null,
          approvalState: matchedTimecard?.approved ? "approved" : "needs_review",
          approvalSource: matchedTimecard?.approvedBy === "system" ? "system" : (matchedTimecard?.approved ? "manager" : null),
          approvalBy: matchedTimecard?.approvedBy || null,
          hasExceptions: matchedTimecard?.hasExceptions === true || ((matchedTimecard?.exceptionFlags || []).length > 0),
          exceptionFlags: matchedTimecard?.exceptionFlags || [],
          locked: matchedTimecard?.locked === true || matchedTimecard?.payrollLocked === true,
        };

        // Apply filters
        if (selectedEmployee !== "all" && shift.employeeId !== selectedEmployee) continue;
        if (selectedLocation !== "all" && shift.locationId !== selectedLocation) continue;
        if (approvalFilter === "needs_review" && shift.approvalState !== "needs_review") continue;

        shifts.push(shift);
      }
    });

    return shifts.sort((a, b) => b.date - a.date);
  };

  const handleApproveTimecard = async (shift) => {
    if (!shift?.timecardId) return;
    try {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('tenantId not resolved');
      const approveTimecard = httpsCallable(functions, 'approveTimecard');
      await approveTimecard({ tenantId, timecardId: shift.timecardId });
      setNotification({ open: true, message: 'Timecard approved', severity: 'success' });
      await loadTimeData();
    } catch (err) {
      console.error('approve timecard error', err);
      setNotification({ open: true, message: 'Could not approve timecard', severity: 'error' });
    }
  };

  const calculateHours = (clockIn, clockOut) => {
    const diffMs = clockOut - clockIn;
    const diffHours = diffMs / (1000 * 60 * 60);
    return Math.round(diffHours * 100) / 100; // Round to 2 decimal places
  };

  // Helpers to validate break time is during the shift
  const parseTimeStringToDate = (baseDate, timeStr) => {
    if (!timeStr) return null;
    const [hh, mm] = timeStr.split(':').map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    const d = new Date(baseDate);
    d.setHours(hh, mm, 0, 0);
    return d;
  };

  const validateBreakWithinShift = (clockIn, clockOut, breakStartTimeStr, breakMinutes) => {
    // If no break provided, OK
    if ((!breakStartTimeStr || breakStartTimeStr === '') && (!breakMinutes || breakMinutes <= 0)) {
      return { valid: true };
    }

    const maxShiftEnd = clockOut || new Date(clockIn.getTime() + 24 * 60 * 60 * 1000); // fallback to 24h max
    const shiftDurationMins = Math.max(0, Math.round((maxShiftEnd - clockIn) / 60000));

    if (breakMinutes && breakMinutes > shiftDurationMins) {
      return { valid: false, error: `Break length (${breakMinutes} mins) exceeds shift length (${shiftDurationMins} mins)` };
    }

    if (breakStartTimeStr && breakStartTimeStr !== '') {
      // Build a candidate break start based on the clockIn date
      let candidate = parseTimeStringToDate(clockIn, breakStartTimeStr);
      if (!candidate) return { valid: false, error: 'Invalid break start time' };

      // If candidate is before clockIn, try rolling it to the next day when a clockOut exists
      if (candidate < clockIn) {
        if (clockOut) {
          const candidateNext = new Date(candidate.getTime() + 24 * 60 * 60 * 1000);
          // If rolling to next day places the break inside the shift window, accept it
          if (candidateNext >= clockIn && candidateNext <= maxShiftEnd) {
            candidate = candidateNext;
          } else {
            return { valid: false, error: 'Break must start at or after clock-in time' };
          }
        } else {
          return { valid: false, error: 'Break must start at or after clock-in time' };
        }
      }

      const breakEnd = new Date(candidate.getTime() + (breakMinutes || 0) * 60 * 1000);
      if (breakEnd > maxShiftEnd) return { valid: false, error: 'Break must end before shift end time' };
    }

    return { valid: true };
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    if (!employee) return "Unknown Employee";
    return `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || "Unknown Employee";
  };

  const getLocationName = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    return location?.name || "Unknown Location";
  };

  const getRoleName = (roleId) => {
    if (!roleId) return "Staff";
    
    // First, try to find the role in the Firestore roles collection
    const role = roles.find(r => r.id === roleId);
    if (role && role.name) {
      return role.name;
    }
    
    // Enhanced role name mapping with more comprehensive coverage (fallback)
    const roleMap = {
      'server': 'Server',
      'cook': 'Cook',
      'host': 'Host',
      'hostess': 'Hostess',
      'manager': 'Manager',
      'shift_lead': 'Shift Lead',
      'shift_leader': 'Shift Leader',
      'assistant_manager': 'Assistant Manager',
      'cashier': 'Cashier',
      'bartender': 'Bartender',
      'busser': 'Busser',
      'dishwasher': 'Dishwasher',
      'prep_cook': 'Prep Cook',
      'line_cook': 'Line Cook',
      'sous_chef': 'Sous Chef',
      'head_chef': 'Head Chef',
      'delivery_driver': 'Delivery Driver',
      'food_runner': 'Food Runner',
      'barista': 'Barista',
      'supervisor': 'Supervisor',
      'team_lead': 'Team Lead',
      'sales_associate': 'Sales Associate',
      'kitchen_staff': 'Kitchen Staff'
    };
    
    // If it's a mapped role code, return the friendly name
    if (roleMap[roleId.toLowerCase()]) {
      return roleMap[roleId.toLowerCase()];
    }
    
    // If it looks like a Firestore ID (long random string), return generic role
    if (roleId.length > 15 && /^[a-zA-Z0-9]+$/.test(roleId)) {
      return 'Staff';
    }
    
    // Otherwise, try to format it nicely (convert underscores to spaces and capitalize)
    return roleId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], {
  hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatWeekday = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };
  const formatDateKeyLabel = (dateKey) => {
    if (!dateKey) return "";
    const d = new Date(`${dateKey}T00:00:00`);
    if (Number.isNaN(d.getTime())) return String(dateKey);
    return formatDate(d);
  };

  const formatHours = (hours) => {
    if (hours === 0) return "0 hrs";
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    
    if (minutes === 0) return `${wholeHours} hrs`;
    return `${wholeHours} hrs ${minutes} mins`;
  };

  const formatBreakMinutes = (minutes) => {
    const safeMinutes = Math.max(0, Math.round(Number(minutes) || 0));
    return `${safeMinutes} min${safeMinutes === 1 ? '' : 's'}`;
  };
  const formatBreakSummary = (shift) => {
    const minutes = Math.max(0, Math.round(Number(shift?.takenBreakMinutes) || 0));
    if (!minutes) return '0 mins';

    const breakType = breakTypes.find((bt) => bt.id === shift?.breakTypeId);
    const breakLabel = breakType?.paid ? 'Paid' : (breakType?.name || 'Break');
    if (!shift?.breakStartTime || !shift?.date) {
      return `${breakLabel} - ${formatBreakMinutes(minutes)}`;
    }

    const start = parseTimeStringToDate(shift.date, shift.breakStartTime);
    if (!start) return `${breakLabel} - ${formatBreakMinutes(minutes)}`;
    const end = new Date(start.getTime() + minutes * 60 * 1000);
    return `${breakLabel} - ${formatBreakMinutes(minutes)} (${formatTime(start)} - ${formatTime(end)})`;
  };

  const formatScheduledTime = (start, end) => {
    if (!start && !end) return 'Scheduled';
    const fmt = (t) => {
      if (!t) return '';
      if (typeof t === 'string') {
        // If string looks like HH:MM (24h), convert to 12h like '8:00 AM'
        const m = t.match(/^(\d{1,2}):(\d{2})$/);
        if (m) {
          const hh = Number(m[1]);
          const mm = Number(m[2]);
          const d = new Date();
          d.setHours(hh, mm, 0, 0);
          return formatTime(d);
        }
        // try parsing as Date string otherwise
        try { return formatTime(new Date(t)); } catch (e) { return String(t); }
      }
      try { return formatTime(new Date(t)); } catch (e) { return String(t); }
    };
    if (start && end) return `${fmt(start)} - ${fmt(end)}`;
    return start ? fmt(start) : (end ? fmt(end) : 'Scheduled');
  };
  const totalShifts = shiftData.length;
  const needsReviewCount = shiftData.filter((s) => s.approvalState !== "approved").length;
  const missingClockOutCount = shiftData.filter((s) => s.isMissingClockOut).length;
  const avgHoursPerShift = totalShifts > 0 ? Math.round((totalHours / totalShifts) * 10) / 10 : 0;
  const approvableShifts = shiftData.filter((s) => s.approvalState !== "approved" && s.timecardId && !s.locked);

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        mt: 3,
        mb: 5,
        px: { xs: 2, sm: 2.5, md: 3, lg: 4 }
      }}
    >
      {/* Header */}
      <Paper
        variant="outlined"
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          background: "linear-gradient(135deg, rgba(25,118,210,0.10) 0%, rgba(25,118,210,0.03) 42%, rgba(255,255,255,0.95) 100%)"
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44, boxShadow: 2 }}>
            <ClockIcon sx={{ color: 'white' }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ lineHeight: 1.15, mb: 0.5 }}>
              Employee Timesheet
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Clear daily timecards with worked hours, break summary, and approvals.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
            <Button
              variant="outlined"
              size="large"
              disabled={bulkApproveLoading || approvableShifts.length === 0}
              onClick={async () => {
                if (approvableShifts.length === 0) {
                  setNotification({ open: true, message: 'No visible timecards need approval', severity: 'info' });
                  return;
                }
                try {
                  setBulkApproveLoading(true);
                  const tenantId = await tryResolveTenantId();
                  if (!tenantId) throw new Error('tenantId not resolved');
                  const approveTimecard = httpsCallable(functions, 'approveTimecard');
                  const results = await Promise.allSettled(
                    approvableShifts.map((shift) => approveTimecard({ tenantId, timecardId: shift.timecardId }))
                  );
                  const successCount = results.filter((result) => result.status === 'fulfilled').length;
                  const failedCount = results.length - successCount;
                  if (successCount > 0) {
                    setNotification({
                      open: true,
                      message: failedCount > 0
                        ? `${successCount} timecards approved, ${failedCount} failed`
                        : `${successCount} timecards approved`,
                      severity: failedCount > 0 ? 'warning' : 'success'
                    });
                  } else {
                    setNotification({ open: true, message: 'Could not approve visible timecards', severity: 'error' });
                  }
                  await loadTimeData();
                } catch (err) {
                  console.error('bulk approve timecards error', err);
                  setNotification({ open: true, message: 'Could not approve visible timecards', severity: 'error' });
                } finally {
                  setBulkApproveLoading(false);
                }
              }}
              sx={{ px: 2.25, whiteSpace: 'nowrap' }}
            >
              {bulkApproveLoading ? 'Approving...' : `Approve All${approvableShifts.length ? ` (${approvableShifts.length})` : ''}`}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => setAddShiftDialog({ open: true })}
              sx={{ px: 2.5, whiteSpace: 'nowrap' }}
            >
              Add Shift
            </Button>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
          <Chip size="small" label={`${totalShifts} shifts`} color="primary" variant="filled" />
          <Chip size="small" label={`Total ${formatHours(totalHours)}`} variant="outlined" />
          <Chip size="small" label={`Avg ${avgHoursPerShift} hrs/shift`} variant="outlined" />
          <Chip
            size="small"
            label={`${needsReviewCount} need review`}
            color={needsReviewCount > 0 ? "warning" : "success"}
            variant={needsReviewCount > 0 ? "filled" : "outlined"}
          />
          <Chip
            size="small"
            label={`${missingClockOutCount} missing clock-out`}
            color={missingClockOutCount > 0 ? "error" : "default"}
            variant={missingClockOutCount > 0 ? "filled" : "outlined"}
          />
        </Stack>
      </Paper>

      {/* Filters */}
      <Paper variant="outlined" sx={{ mb: 3, p: 2.25, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid>
            <FormControl fullWidth size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRangeMode}
                onChange={(e) => setDateRangeMode(e.target.value)}
                label="Date Range"
              >
                <MenuItem value="current" disabled={payPeriods.length === 0}>Current Pay Period</MenuItem>
                <MenuItem value="previous" disabled={payPeriods.length === 0}>Previous Pay Period</MenuItem>
                <MenuItem value="next" disabled={payPeriods.length === 0}>Next Pay Period</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
            {dateRangeMode === 'custom' ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <TextField
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="small"
                  fullWidth
                />
                <Typography variant="body2">→</Typography>
                <TextField
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {selectedPayPeriod
                  ? `Pay Period: ${formatDateKeyLabel(selectedPayPeriod.startDateKey)} - ${formatDateKeyLabel(selectedPayPeriod.endDateKey)} (${String(selectedPayPeriod.status || 'open').toUpperCase()})`
                  : 'No pay periods found. Switch to Custom Range to set dates manually.'}
              </Typography>
            )}
          </Grid>

          <Grid>
            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>
              <Select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                label="Employee"
              >
                <MenuItem value="all">All Employees</MenuItem>
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {getEmployeeName(employee.id)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid>
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                label="Location"
              >
                <MenuItem value="all">All Locations</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid>
            <FormControl fullWidth size="small">
              <InputLabel>Approval</InputLabel>
              <Select
                value={approvalFilter}
                onChange={(e) => setApprovalFilter(e.target.value)}
                label="Approval"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="needs_review">Needs Review</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Time Entries Table */}
      <Paper variant="outlined" sx={{ width: '100%', borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography>Loading timesheet data...</Typography>
          </Box>
        ) : shiftData.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No time entries found for the selected criteria.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'hidden' }}>
            <Table
              stickyHeader
              sx={{
                tableLayout: 'fixed',
                borderCollapse: 'separate',
                borderSpacing: 0,
                '& th': {
                  whiteSpace: 'normal',
                  bgcolor: 'grey.100',
                  color: 'text.secondary',
                  fontWeight: 700,
                  fontSize: '0.74rem',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  lineHeight: 1.15,
                  px: 0.7,
                  py: 0.8,
                  top: 0,
                  zIndex: 1,
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider'
                },
                '& td': {
                  fontSize: '0.8rem',
                  verticalAlign: 'top',
                  py: 0.75,
                  px: 0.7,
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider'
                }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '12%' }}>Employee</TableCell>
                  <TableCell sx={{ width: '9%' }}>Date</TableCell>
                  <TableCell sx={{ width: '10%' }}>Location</TableCell>
                  <TableCell sx={{ width: '6%' }}>Role</TableCell>
                  <TableCell sx={{ width: '7%' }}>Punch In</TableCell>
                  <TableCell sx={{ width: '7%' }}>Punch Out</TableCell>
                  <TableCell sx={{ width: '7%' }}>Breaks</TableCell>
                  <TableCell sx={{ width: '5%' }}>Wage</TableCell>
                  <TableCell sx={{ width: '15%' }}>Manager Approval</TableCell>
                  <TableCell sx={{ width: '6%' }}>Hours</TableCell>
                  <TableCell align="center" sx={{ width: '5%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shiftData.map((shift, idx) => (
                  <TableRow
                    key={shift.id}
                    sx={{
                      bgcolor: shift.isMissingClockOut
                        ? 'error.50'
                        : (shift.approvalState === 'needs_review'
                          ? 'warning.50'
                          : (idx % 2 === 0 ? 'background.paper' : 'grey.50')),
                      '&:hover': {
                        bgcolor: shift.isMissingClockOut
                          ? 'error.100'
                          : (shift.approvalState === 'needs_review'
                            ? 'warning.100'
                            : 'action.hover')
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.74rem', lineHeight: 1.2 }}>
                        {shift.employeeName || getEmployeeName(shift.employeeId)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 0.15, fontSize: '0.66rem', lineHeight: 1.05 }}>
                        {formatWeekday(shift.date)}
                      </Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.74rem', lineHeight: 1.15 }}>
                        {formatDate(shift.date)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.74rem', lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                        {getLocationName(shift.locationId)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.72rem', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                        {shift.displayRoleId ? getRoleName(shift.displayRoleId) : getRoleName(shift.roleId)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ color: shift.clockIn ? 'text.primary' : 'text.disabled', fontWeight: 600, fontSize: '0.74rem', whiteSpace: 'nowrap' }}
                      >
                        {shift.clockIn ? formatTime(shift.clockIn) : '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {shift.isMissingClockOut ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <WarningIcon color="error" fontSize="small" />
                          <Typography variant="body2" color="error" fontWeight={700}>
                            Missing
                          </Typography>
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: shift.clockOut ? 'text.primary' : 'text.disabled', fontWeight: 600, fontSize: '0.74rem', whiteSpace: 'nowrap' }}
                        >
                          {shift.clockOut ? formatTime(shift.clockOut) : '—'}
                        </Typography>
                      )}
                      {shift.isAutoClockOut && (
                        <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.1, fontWeight: 600, fontSize: '0.64rem' }}>
                          Auto
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ lineHeight: 1.1, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                        {formatBreakSummary(shift)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                        ${Number(shift.wage || 0).toFixed(2)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ pr: 0.35 }}>
                      <Stack spacing={0.15} alignItems="flex-start">
                        <Chip
                          label={shift.approvalState === "approved" ? "Approved" : "Needs Review"}
                          color={shift.approvalState === "approved" ? "success" : "warning"}
                          size="small"
                          icon={shift.approvalState === "approved" ? <CheckCircleIcon /> : <WarningIcon />}
                          sx={{
                            height: 24,
                            '& .MuiChip-label': {
                              px: 0.75,
                              fontSize: '0.68rem',
                              fontWeight: 700
                            },
                            '& .MuiChip-icon': {
                              fontSize: '0.82rem'
                            }
                          }}
                        />
                        {shift.approvalState === "approved" ? (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.64rem', lineHeight: 1.1 }}>
                            {shift.approvalSource === "system"
                              ? "Auto-approved"
                              : "Manager approved"}
                          </Typography>
                        ) : null}
                        {shift.approvalState !== "approved" && shift.timecardId && !shift.locked ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleApproveTimecard(shift)}
                            sx={{ minWidth: 0, px: 0.9, py: 0.25, fontSize: '0.68rem', lineHeight: 1 }}
                          >
                            Approve
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ pl: 0.35 }}>
                      <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.74rem', whiteSpace: 'nowrap' }}>
                        {formatHours(shift.hours)}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.1, justifyContent: 'center', minWidth: 46 }}>
                        <Tooltip title="Edit Times">
                          <IconButton
                            size="small"
                            sx={{ p: 0.25 }}
                            onClick={() => handleEditPunch(shift)}
                          >
                            <EditIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Shift">
                          <IconButton
                            size="small"
                            sx={{ p: 0.25 }}
                            onClick={() => setDeleteDialog({ open: true, shift })}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Total Row */}
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell colSpan={9}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Total
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {formatHours(totalHours)}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Missing Clock-Outs Alert */}
      {shiftData.some(shift => shift.isMissingClockOut) && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Missing Clock-Outs Detected
          </Typography>
          <Typography variant="body2">
            Some employees are missing clock-out entries. Employees are automatically clocked out after 20 hours.
          </Typography>
        </Alert>
      )}

      {/* Notification Alert */}
      {notification.open && (
        <Alert 
          severity={notification.severity} 
          sx={{ mt: 2 }}
          onClose={() => setNotification({ open: false, message: "", severity: "success" })}
        >
          {notification.message}
        </Alert>
      )}

      {/* Edit Time Entry Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, shift: null, type: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Shift Times - {editDialog.shift?.employeeName}
        </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Clock In Time"
              type="datetime-local"
              value={editDateTime}
              onChange={(e) => setEditDateTime(e.target.value)}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              required
            />
            
            <TextField
              fullWidth
              label="Clock Out Time (Optional)"
              type="datetime-local"
              value={editClockOutDateTime}
              onChange={(e) => setEditClockOutDateTime(e.target.value)}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty if employee hasn't clocked out yet"
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Location</InputLabel>
              <Select
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                label="Location"
              >
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={editRole ?? ''}
                onChange={(e) => setEditRole(e.target.value || '')}
                label="Role"
                renderValue={(val) => val ? getRoleName(val) : ''}
              >
                {(() => {
                  // Roles visible for selection: non-archived and match the selected editLocation (if set)
                  const visibleRoles = (roles || []).filter(r => !r.archived && (!editLocation || r.locationId === editLocation));
                  const selectedRole = (roles || []).find(r => r.id === editRole);

                  // Build an explicit array of MenuItem elements (avoids returning a Fragment which MUI warns about)
                  const items = [];
                  if (!visibleRoles.find(r => r.id === editRole) && selectedRole) {
                    items.push(
                      <MenuItem key={`current-${selectedRole.id}`} value={selectedRole.id} disabled>
                        {selectedRole.archived ? `${selectedRole.name} (Archived)` : `${selectedRole.name} (Not available for this location)`}
                      </MenuItem>
                    );
                  }

                  visibleRoles.forEach((role) => items.push(
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ));

                  return items;
                })()}
              </Select>
            </FormControl>

            

            {/* Break editing fields */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
              <FormControl sx={{ minWidth: 260 }}>
                <InputLabel>Break</InputLabel>
                <Select
                  value={editBreakTypeId}
                  label="Break"
                  onChange={(e) => {
                    setEditBreakTypeId(e.target.value);
                    const bt = breakTypes.find(b => b.id === e.target.value);
                    if (bt) setEditBreakMinutes(bt.minutes || 0);
                  }}
                >
                  <MenuItem value="">No Break</MenuItem>
                  {breakTypes.map(bt => (
                    <MenuItem key={bt.id} value={bt.id}>{`${bt.name} - ${bt.minutes} minutes${bt.paid ? ' (Paid)' : ''}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Start time (optional)"
                type="time"
                value={editBreakStartTime}
                onChange={(e) => setEditBreakStartTime(e.target.value)}
                sx={{ width: 160 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              fullWidth
              label="Break Minutes"
              type="number"
              value={editBreakMinutes}
              onChange={(e) => setEditBreakMinutes(Number(e.target.value || 0))}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            {editBreakError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>{editBreakError}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, shift: null, type: null })}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            disabled={editLoading || !!editBreakError}
            startIcon={editLoading ? <CircularProgress size={16} /> : null}
          >
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Shift Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, shift: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this entire shift for {deleteDialog.shift?.employeeName}? 
            This will remove both clock-in and clock-out entries and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, shift: null })}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteShift} 
            color="error" 
            variant="contained" 
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={16} /> : null}
          >
            {editLoading ? 'Deleting...' : 'Delete Shift'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Shift Dialog */}
      <Dialog open={addShiftDialog.open} onClose={() => setAddShiftDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add New Shift
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Employee</InputLabel>
              <Select
                value={addEmployee}
                onChange={(e) => {
                  setAddEmployee(e.target.value);
                  setAddRoleId(""); // Clear selected role when employee changes
                }}
                label="Employee"
                required
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {`${employee.firstName || ''} ${employee.lastName || ''}`.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Date"
              type="date"
              value={addDate}
              onChange={(e) => setAddDate(e.target.value)}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              fullWidth
              label="Clock In Time"
              type="time"
              value={addClockInTime}
              onChange={(e) => setAddClockInTime(e.target.value)}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              required
            />
            
            <TextField
              fullWidth
              label="Clock Out Time (Optional)"
              type="time"
              value={addClockOutTime}
              onChange={(e) => setAddClockOutTime(e.target.value)}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty to create only clock-in entry"
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
              <FormControl sx={{ minWidth: 260 }}>
                <InputLabel>Break</InputLabel>
                <Select
                  value={addBreakTypeId}
                  label="Break"
                  onChange={(e) => {
                    setAddBreakTypeId(e.target.value);
                    // if selecting a break type, populate minutes
                    const bt = breakTypes.find(b => b.id === e.target.value);
                    if (bt) setAddBreakMinutes(bt.minutes || 0);
                  }}
                >
                  <MenuItem value="">No Break</MenuItem>
                  {breakTypes.map(bt => (
                    <MenuItem key={bt.id} value={bt.id}>{`${bt.name} - ${bt.minutes} minutes${bt.paid ? ' (Paid)' : ''}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Start time (optional)"
                type="time"
                value={addBreakStartTime}
                onChange={(e) => setAddBreakStartTime(e.target.value)}
                sx={{ width: 160 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            {addBreakError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>{addBreakError}</Typography>
            )}
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Location</InputLabel>
              <Select
                value={addLocationId}
                onChange={(e) => setAddLocationId(e.target.value)}
                label="Location"
                required
              >
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={addRoleId}
                onChange={(e) => setAddRoleId(e.target.value)}
                label="Role"
                required
                disabled={!addEmployee}
              >
                {!addEmployee ? (
                  <MenuItem value="" disabled>
                    Please select an employee first
                  </MenuItem>
                ) : getEmployeeRoles().length === 0 ? (
                  <MenuItem value="" disabled>
                    No roles assigned to this employee
                  </MenuItem>
                ) : (
                  getEmployeeRoles().map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            {/* ...existing code... */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddShiftDialog({ open: false })}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddShift} 
            variant="contained" 
            disabled={editLoading || !!addBreakError}
            startIcon={editLoading ? <CircularProgress size={16} /> : null}
          >
            {editLoading ? 'Adding...' : 'Add Shift'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
